import * as React from 'react';
import { Check, Lock, Send, Upload, Zap } from 'lucide-react';
import { WorkspaceButton } from '@/components/ui/workspace';
import type { DiscoverySummary } from '@/lib/api/discovery';
import { useInterviewsQuery } from '@/lib/api/discovery-sessions';
import { useCanCurateFrameworks } from '@/lib/api/engagement-lifecycle';
import { cn } from '@/lib/utils/cn';
import { ClientFactsDialog } from '../client-facts/client-facts';
import { useDiscoveryNavigate } from '../discovery-layout';
import { guideSteps, type GuideAction, type GuideStepView } from '../discovery-logic';
import { listTitle } from '../sessions/sessions-logic';

/**
 * The guided start (T-0185; Decisions 47 and 54.9; four steps since T-0262,
 * E23-design §9). Until evidence is first confirmed, Overview is this
 * guide: the steps in order, the one to do now in orange, and what opens
 * the rest. Each step's state and counts come from the API (`guideState`):
 * steps tick independently and exactly one is current. Prototype:
 * `guideView` in `docs/discovery/prototype/`.
 */

const STATE_WORD: Record<GuideStepView['state'], string> = {
  done: 'Done',
  current: 'Do this now',
  todo: 'Open',
  locked: 'Not open yet',
};

const LINKISH =
  'mt-1 inline-flex rounded-chip text-meta font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

export function DiscoveryGuide({
  engagementId,
  summary,
}: {
  engagementId: string;
  summary: DiscoverySummary;
}) {
  const go = useDiscoveryNavigate(engagementId);
  const canCurateFrameworks = useCanCurateFrameworks();
  const interviews = useInterviewsQuery(engagementId);
  const [factsOpen, setFactsOpen] = React.useState(false);
  const run = (a: GuideAction) => {
    if (a.kind === 'client_facts') setFactsOpen(true);
    else if (a.kind === 'review') go({ to: 'sessions', review: 'all' });
    else if (a.kind === 'intake') go({ to: 'sessions', intake: true });
    else if (a.kind === 'interview') go({ to: 'sessions', interview: a.id });
    else go({ to: a.kind });
  };
  // The interviews whose items wait on their speakers, named as the list names them.
  const matchSpeakers = (interviews.data?.groups ?? [])
    .flatMap((g) => g.items)
    .filter((i) => i.state.kind === 'match_speakers')
    .map((i) => ({ id: i.id, name: listTitle(i) }));
  const steps = guideSteps(summary, { canCurateFrameworks, matchSpeakers });

  return (
    <div className="min-h-full bg-paper-50 px-6 pb-8 pt-4">
      <div className="max-w-[1120px]">
        <div className="mb-4">
          <h2 className="text-h2 font-semibold text-ink-900">Where to start</h2>
          <p className="text-meta font-medium text-ink-500">
            Discovery runs in this order. Do the step in orange; the rest open as you go.
          </p>
        </div>
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <ol
            aria-label="Discovery steps"
            className="relative before:absolute before:bottom-3.5 before:left-[13px] before:top-3.5 before:w-0.5 before:bg-ink-100"
          >
            {steps.map((step, i) => (
              <li
                key={step.key}
                data-state={step.state}
                aria-current={step.state === 'current' ? 'step' : undefined}
                className="relative mb-3"
              >
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'relative z-[1] inline-flex h-7 w-7 flex-none items-center justify-center rounded-full border-2 text-meta font-semibold',
                      step.state === 'done' &&
                        'border-posture-healthy bg-posture-healthy text-paper-0',
                      step.state === 'current' &&
                        'border-accent-amber-600 bg-accent-amber-600 text-paper-0',
                      step.state === 'todo' && 'border-ink-200 bg-paper-0 text-ink-500',
                      step.state === 'locked' && 'border-ink-100 bg-paper-0 text-ink-300',
                    )}
                  >
                    {step.state === 'done' ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : step.state === 'locked' ? (
                      <Lock className="h-3 w-3" />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span
                    className={cn(
                      'text-[15px] font-semibold',
                      step.state === 'todo'
                        ? 'text-ink-500'
                        : step.state === 'locked'
                          ? 'text-ink-300'
                          : 'text-ink-900',
                    )}
                  >
                    {step.title}
                    <span className="sr-only">: {STATE_WORD[step.state]}</span>
                  </span>
                </div>
                <div
                  className={cn(
                    'ml-10',
                    step.state === 'current'
                      ? 'mt-2 rounded-card border border-l-4 border-accent-amber-100 border-l-accent-amber-600 bg-paper-0 px-4 py-3.5'
                      : 'mt-1 pb-1.5',
                  )}
                >
                  <p
                    className={cn(
                      'text-body-sm',
                      step.state === 'locked' ? 'text-ink-300' : 'text-ink-700',
                    )}
                  >
                    {step.text}
                  </p>
                  {step.hint ? (
                    <p className="text-meta font-medium text-ink-500">{step.hint}</p>
                  ) : null}
                  {step.ai ? (
                    <p
                      className={cn(
                        'mt-1.5 flex gap-1.5 text-meta text-ai-ink',
                        step.state === 'locked' && 'opacity-70',
                      )}
                    >
                      <Zap className="mt-[3px] h-3 w-3 flex-none" aria-hidden="true" />
                      <span>{step.ai}</span>
                    </p>
                  ) : null}
                  {step.actions.length > 0 && step.state === 'current' ? (
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      {step.actions.map((a, n) =>
                        n === 0 ? (
                          <WorkspaceButton key={a.label} variant="primary" onClick={() => run(a)}>
                            {a.label}
                          </WorkspaceButton>
                        ) : a.kind === 'intake' ? (
                          <WorkspaceButton key={a.label} onClick={() => run(a)}>
                            {a.label}
                          </WorkspaceButton>
                        ) : (
                          <button
                            key={a.label}
                            type="button"
                            className={cn(LINKISH, 'mt-0')}
                            onClick={() => run(a)}
                          >
                            {a.label}
                          </button>
                        ),
                      )}
                    </div>
                  ) : step.actions.length > 0 ? (
                    <span className="flex flex-wrap gap-3">
                      {step.actions.map((a) => (
                        <button
                          key={a.label}
                          type="button"
                          className={LINKISH}
                          onClick={() => run(a)}
                        >
                          {a.label}
                        </button>
                      ))}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
          <aside className="flex flex-col gap-2.5 lg:sticky lg:top-0">
            <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-500">
              Alongside the interviews
            </div>
            <SideCard
              icon={<Send className="h-4 w-4" />}
              title="Send the department questionnaire"
              text="For the departments you won’t interview: a short questionnaire by secure link instead of a meeting. Each answer counts as evidence from the person who sends it. Your key interviews still go ahead."
            >
              <WorkspaceButton onClick={() => go({ to: 'sessions' })}>
                Set it up in Sessions
              </WorkspaceButton>
            </SideCard>
            <SideCard
              icon={<Upload className="h-4 w-4" />}
              title="Upload documents the client already has"
              text="Budget book, chart of accounts, scheduled-job lists. A document counts as an independent source."
            >
              <WorkspaceButton onClick={() => go({ to: 'sessions', intake: true })}>
                Bring in files
              </WorkspaceButton>
            </SideCard>
            <div className="mt-4 text-[11px] font-medium uppercase tracking-[0.06em] text-ink-500">
              How AI helps
            </div>
            <p className="text-body-sm text-ink-700">
              AI reads, sorts and drafts. You confirm. Code does all the counting, so every number
              is typed by a person, said by the client, or counted.
            </p>
          </aside>
        </div>
      </div>
      <ClientFactsDialog engagementId={engagementId} open={factsOpen} onOpenChange={setFactsOpen} />
    </div>
  );
}

function SideCard({
  icon,
  title,
  text,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3 rounded-card border border-ink-100 bg-paper-0 p-3.5">
      <span className="mt-0.5 flex-none text-ink-500" aria-hidden="true">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-body-sm font-medium text-ink-900">{title}</div>
        <p className="text-meta font-medium text-ink-500">{text}</p>
        <div className="mt-2 flex gap-1.5">{children}</div>
      </div>
    </div>
  );
}
